import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Bell, ChevronRight, Download, Info, RefreshCw, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
// Discussion section hidden for now
// import GmpDiscussion from '../components/gmp/GmpDiscussion';
import SmeStatCards from '../components/sme-ipos/SmeStatCards';
import SmeEssentialsGuide from '../components/sme-ipos/SmeEssentialsGuide';
import SmeControls from '../components/sme-ipos/SmeControls';
import SmeIposTable from '../components/sme-ipos/SmeIposTable';
import SmeSidebar from '../components/sme-ipos/SmeSidebar';
import { PLATFORMS, SME_TABS, buildSmeCsv, buildSmeIpoRows, computeSmeStats, filterSmeIpos, smeTabByKey } from '../utils/smeIpos';
import { formatRefreshedAt } from '../utils/gmpBoard';
import { todayIso } from '../utils/ipoCalendar';
import { fetchSmeIpos } from '../store/slices/smeIposSlice';
import { fetchSmeUpcomingIpos } from '../store/slices/upcomingIposSlice';
import { fetchSmeGmp, selectSmeGmpBoardRows } from '../store/slices/smeGmpSlice';
import { fetchSmeSubscriptionSummary, selectSmeSubscriptionRows } from '../store/slices/subscriptionSlice';

const NOTICE_MS = 4500;
const DATA_MAX_AGE_MS = 60000;
const YEAR = String(new Date().getFullYear());
const HEADING = `SME IPO List ${YEAR}: NSE Emerge & BSE SME`;

// const DISCUSSION_COPY = {
//   headingId: 'sme-discussion-heading',
//   title: 'SME IPO Discussion & Live Sentiment',
//   subtitle: 'Discuss lot sizes, GMP moves and listing expectations for SME issues with fellow investors.',
//   placeholder: 'Share your view on an SME issue, GMP observations, or ask about lot sizes and allotment...',
//   submitLabel: 'Post Comment',
//   startLabel: 'Start New Discussion',
// };

export const SmeIposPage = () => {
  const dispatch = useDispatch();
  const today = useMemo(() => todayIso(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = smeTabByKey(searchParams.get('tab')).key;
  const [platform, setPlatform] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState(null);
  const tableRef = useRef(null);

  // GET /api/core/ipos/sme is the list; the SME schedule, GMP and subscription
  // feeds add symbol, price band, allotment date, GMP and live demand
  const smeList = useSelector((state) => state.smeIpos.items);
  const listStatus = useSelector((state) => state.smeIpos.status);
  const listError = useSelector((state) => state.smeIpos.error);
  const listRefreshedAt = useSelector((state) => state.smeIpos.refreshedAt);
  const scheduleItems = useSelector((state) => state.smeUpcomingIpos.items);
  const gmpRows = useSelector(selectSmeGmpBoardRows);
  const subscriptionRows = useSelector(selectSmeSubscriptionRows);

  const loadFeeds = (force = false) => {
    const options = force ? {} : { maxAgeMs: DATA_MAX_AGE_MS };
    dispatch(fetchSmeIpos(options));
    dispatch(fetchSmeUpcomingIpos(options));
    dispatch(fetchSmeGmp(options));
    dispatch(fetchSmeSubscriptionSummary(options));
  };

  useEffect(() => {
    loadFeeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const SME_IPOS = useMemo(
    () => buildSmeIpoRows({ smeList, liveIpos: scheduleItems, gmpRows, subscriptionRows }, today),
    [smeList, scheduleItems, gmpRows, subscriptionRows, today]
  );
  const stats = useMemo(() => computeSmeStats(SME_IPOS), [SME_IPOS]);
  // Counts follow the search, so a match sitting in another tab or platform shows where it is
  const searched = useMemo(() => filterSmeIpos(SME_IPOS, { query }), [SME_IPOS, query]);
  const tabCounts = useMemo(
    () => Object.fromEntries(SME_TABS.map((option) => [option.key, searched.filter(option.test).length])),
    [searched]
  );
  const platformCounts = useMemo(() => {
    const inTab = searched.filter(smeTabByKey(tab).test);
    return Object.fromEntries(
      ['all', ...Object.keys(PLATFORMS)].map((key) => [key, key === 'all' ? inTab.length : inTab.filter((row) => row.exchange === key).length])
    );
  }, [searched, tab]);
  const rows = useMemo(() => filterSmeIpos(SME_IPOS, { tab, exchange: platform, query }), [SME_IPOS, tab, platform, query]);

  const isLoading = !SME_IPOS.length && (listStatus === 'idle' || listStatus === 'loading');
  const listFailed = !SME_IPOS.length && listStatus === 'failed';
  const refreshedLabel = formatRefreshedAt(listRefreshedAt);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADING} | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [tab, platform, query]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const changeTab = (key) => setSearchParams(key === 'all' ? {} : { tab: key }, { replace: true });

  const clearFilters = () => {
    setPlatform('all');
    setQuery('');
  };

  const changePage = (nextPage) => {
    setPage(nextPage);
    // Paging from the bottom of a long table: bring its first row back into view
    if (tableRef.current && tableRef.current.getBoundingClientRect().top < 0) {
      tableRef.current.scrollIntoView({ block: 'start' });
    }
  };

  const handleExport = () => {
    // BOM so spreadsheet apps read ₹ correctly
    const blob = new Blob(['﻿', buildSmeCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sme-ipos-${tab}-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  let emptyMessage;
  if (listFailed) {
    emptyMessage = (
      <>
        SME IPO data is temporarily unavailable{listError ? ` (${listError})` : ''}.{' '}
        <button type="button" onClick={() => loadFeeds(true)} className="font-bold text-[#008080] hover:underline">
          Retry
        </button>
      </>
    );
  } else if (!SME_IPOS.length) {
    emptyMessage = 'No SME IPOs are open or scheduled right now. New issues appear here as soon as their dates are announced.';
  } else if (platform !== 'all' || query) {
    emptyMessage = (
      <>
        No SME IPOs match these filters.{' '}
        <button type="button" onClick={clearFilters} className="font-bold text-[#008080] hover:underline">
          Clear filters
        </button>
      </>
    );
  } else {
    emptyMessage = `No SME IPOs in "${smeTabByKey(tab).label}" right now.`;
  }

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/ipo-dashboard" className="hover:text-[#008080] transition-colors">
                Primary Market Desk
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-900 font-semibold" aria-current="page">
                SME IPO List {YEAR}
              </span>
            </nav>

            <div className="mt-3 flex flex-col xl:flex-row xl:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">{HEADING}</h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] font-bold text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                    Live Exchange Feed
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Every SME IPO listing on <strong className="text-slate-700">NSE Emerge</strong> and{' '}
                  <strong className="text-slate-700">BSE SME</strong>: bidding dates, price band, lot size, issue size, live
                  grey market premium and subscription, from open to listing day.
                  {refreshedLabel && <span className="text-slate-400"> Updated {refreshedLabel}.</span>}
                </p>
              </div>

              {/* <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => loadFeeds(true)}
                  disabled={listStatus === 'loading'}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${listStatus === 'loading' ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={!rows.length}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => setNotice('SME IPO alerts are coming soon. New SME issues will appear on this page as soon as dates are announced.')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-teal-100 bg-teal-50 text-xs font-bold text-[#008080] hover:bg-teal-100 transition-colors"
                >
                  <Bell className="w-3.5 h-3.5" />
                  SME Alerts
                </button>
              </div> */}
            </div>
          </div>

          <SmeStatCards stats={stats} />

          <SmeEssentialsGuide />

          {/* Sidebar sits beside the table only from 1440px, where the table has room.
              Narrower screens stack it between the table and the discussion. */}
          <div className="grid grid-cols-1 min-[1440px]:grid-cols-[minmax(0,1fr)_300px] gap-5 sm:gap-6 items-start">
            <div className="space-y-4 sm:space-y-5 min-w-0">
              <SmeControls
                tab={tab}
                onTabChange={changeTab}
                tabCounts={tabCounts}
                platform={platform}
                onPlatformChange={setPlatform}
                platformCounts={platformCounts}
              />

              <SmeIposTable
                ref={tableRef}
                rows={rows}
                today={today}
                page={page}
                onPageChange={changePage}
                query={query}
                onQueryChange={setQuery}
                isLoading={isLoading}
                emptyMessage={emptyMessage}
              />
            </div>

            <SmeSidebar className="min-[1440px]:col-start-2 min-[1440px]:row-start-1 min-[1440px]:row-span-2" />

            {/* Discussion section hidden for now:
            <div className="min-w-0">
              <GmpDiscussion initialThreads={[]} copy={DISCUSSION_COPY} />
            </div>
            */}

          </div>
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default SmeIposPage;
